import { api } from "@/lib/api";

// Reading progress and voice recordings made without internet wait here (IndexedDB) until they can be sent.
const DB = "yh-outbox";
const STORE = "items";
const EVENT = "yh-outbox-change";

const open = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB, 1);
  req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "key" });
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const run = async (mode, fn) => {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    tx.oncomplete = () => { db.close(); resolve(req?.result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
};

const changed = () => window.dispatchEvent(new Event(EVENT));
export const onOutboxChange = (fn) => {
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
};

export const outboxItems = async () => (typeof indexedDB === "undefined" ? [] : (await run("readonly", (s) => s.getAll())) || []);
// Deletes the item only if it was not replaced by a newer one while it was being sent.
const remove = async (key, at) => {
  await run("readwrite", (s) => {
    const req = s.get(key);
    req.onsuccess = () => { if (!at || req.result?.at === at) s.delete(key); };
    return req;
  });
};

// Called after progress was saved online, so an older offline page cannot overwrite it later.
export const dropProgress = async (bookId) => { if (typeof indexedDB !== "undefined") await remove(`progress:${bookId}`); };

// No reply at all from the server means the device is offline (or the server unreachable).
export const isNetworkError = (e) => !e?.response;

export const queueProgress = async (bookId, page, finished) => {
  await run("readwrite", (s) => s.put({ key: `progress:${bookId}`, type: "progress", book_id: bookId, page, finished, at: new Date().toISOString() }));
  changed();
};

export const queueRecording = async ({ bookId, page, duration, blob }) => {
  const key = `rec:${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await run("readwrite", (s) => s.put({ key, type: "recording", book_id: bookId, page, duration, blob, at: new Date().toISOString() }));
  changed();
};

export const pendingProgress = async () => Object.fromEntries((await outboxItems())
  .filter((i) => i.type === "progress").map((i) => [i.book_id, { page: i.page, finished: i.finished, updated_at: i.at }]));

export const recordingForm = ({ blob, bookId, page, duration, recordedAt }) => {
  const fd = new FormData();
  const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
  fd.append("file", blob, `reading.${ext}`);
  fd.append("book_id", bookId);
  fd.append("page", String(page));
  fd.append("duration", String(Math.max(duration, 0.5)));
  // Sent from the offline queue: lets the teacher know it was recorded without internet.
  if (recordedAt) { fd.append("offline", "true"); fd.append("recorded_at", recordedAt); }
  return fd;
};

const send = (i) => (i.type === "progress"
  ? api.put(`/progress/${i.book_id}`, { page: i.page })
  : api.post("/recordings", recordingForm({ blob: i.blob, bookId: i.book_id, page: i.page, duration: i.duration, recordedAt: i.at })));

let flushing = null;
// Sends everything waiting. Stops at the first network failure; drops items the server refuses (e.g. book deleted).
// Resolves to { sent, dropped }.
export const flushOutbox = () => {
  flushing = flushing || (async () => {
    let sent = 0;
    let dropped = 0;
    try {
      for (const i of await outboxItems()) {
        try {
          await send(i);
          sent += 1;
          await remove(i.key, i.at);
        } catch (e) {
          if (isNetworkError(e)) break;
          if (e.response.status === 401) break;
          dropped += 1;
          await remove(i.key);
        }
      }
    } finally {
      flushing = null;
      if (sent || dropped) changed();
    }
    return { sent, dropped };
  })();
  return flushing;
};
